import '@tamagui/native/setup-safe-area';
import '@tamagui/native/setup-worklets';

import { useCurrentUser, useTenantAvailable } from '@api/application-configuration';
import { axiosClient } from '@api/axios';
import { initAPIInterceptor } from '@api/interceptor';
import { queryClient, setQueryDefaults } from '@api/query-client';
import { getEnvVars } from '@environment';
import NetInfo from '@react-native-community/netinfo';
import * as Sentry from '@sentry/react-native';
import { focusManager, keepPreviousData, onlineManager } from '@tanstack/react-query';
import { Background } from '@unistocks/components/background/background';
import { AppLoader } from '@unistocks/components/loading/app-loader/app-loader';
import { setAxiosFactory } from '@unistocks/proxy/axios-client';
import { useApplicationConfiguration } from '@unistocks/shared/hooks/use-application-configuration';
import { useAuthToken } from '@unistocks/shared/hooks/use-auth-token';
import { useAutomaticUpdates } from '@unistocks/shared/hooks/use-automatic-updates';
import { useDevTools } from '@unistocks/shared/hooks/use-dev-tools';
import { useSentryLogEnricher } from '@unistocks/shared/hooks/use-sentry-log-enricher';
import { useTranslations } from '@unistocks/shared/hooks/use-translations';
import { t } from '@unistocks/shared/localization/index.ts';
import { Logger } from '@unistocks/shared/services/logger.service';
import { useNotificationHub } from '@unistocks/shared/signalr/use-notification-hub';
import { usePortfolioHub } from '@unistocks/shared/signalr/use-portfolio-hub';
import { store } from '@unistocks/shared/store';
import { Stack, useNavigationContainerRef } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { useEffect } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import { configureReanimatedLogger, ReanimatedLogLevel } from 'react-native-reanimated';
import { enableFreeze } from 'react-native-screens';
import { isWeb } from 'tamagui';
import { AppProvider } from '../providers/provider';

const { logLevel, production, sentry } = getEnvVars();

export {
  // Catch any errors thrown by the Layout component.
  ErrorBoundary
} from 'expo-router';

export const unstable_settings = {
  // Ensure that reloading on `/modal` keeps a back button present.
  anchor: '(tabs)'
};

enableFreeze();
initAPIInterceptor(store, queryClient);
setAxiosFactory(() => axiosClient);
setQueryDefaults();

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync().catch(() => {
  // reloading the app might trigger some race conditions, ignore them
});
SplashScreen.setOptions({
  duration: 1000,
  fade: true
});

/**
 * Sentry configuration
 */
const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: true
});

Sentry.init({
  enabled: sentry.enable,
  enableLogs: true,
  environment: sentry.environment,
  dsn: sentry.dsn,
  debug: false,
  tracesSampleRate: production ? 0.1 : 1.0, // Adjust this value in production as needed.
  integrations: [navigationIntegration]
});

/**
 * Logger configuration
 */
Logger.init(logLevel, __DEV__);

// TODO: [Reanimated] Reading from `value` during component render
configureReanimatedLogger({
  level: production ? ReanimatedLogLevel.error : ReanimatedLogLevel.warn,
  strict: false
});

/**
 * Set tanstack query focus state
 */
const onAppStateChange = (status: AppStateStatus) => {
  if (!isWeb) {
    focusManager.setFocused(status === 'active');
  }
};

/**
 * Set tanstack query online state
 * Handled automatically on web
 */
if (!isWeb) {
  onlineManager.setEventListener(setOnline => {
    return NetInfo.addEventListener(state => {
      setOnline(!!state.isConnected);
    });
  });
}

function RootLayout() {
  const ref = useNavigationContainerRef();

  useEffect(() => {
    if (ref.current) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  return (
    <AppProvider>
      <Unistocks />
    </AppProvider>
  );
}

function Unistocks() {
  useDevTools();
  usePortfolioHub();
  useNotificationHub();
  useSentryLogEnricher();
  const { isReady: isAuthReady, isValid: isValidToken } = useAuthToken();
  const { loadingText, isDownloadingUpdate, updateDownloadProgress, didUpdateCheck } =
    useAutomaticUpdates();
  const [{ isLoaded: isTranslationsLoaded, version }] = useTranslations();

  const {
    data: appConfig,
    isSuccess: appConfigSuccess,
    isError: appConfigError
  } = useApplicationConfiguration({
    placeholderData: keepPreviousData
  });

  const currentUser = useCurrentUser();
  const isLoggedIn = isValidToken && !!currentUser?.isAuthenticated;
  const isTenantUser = useTenantAvailable();
  const isHost = !isTenantUser;

  useEffect(() => {
    if (isTranslationsLoaded) {
      // Hide the splash screen after the app config is loaded (or an error was returned) and the UI is ready.
      SplashScreen.hide();

      const subscription = AppState.addEventListener('change', onAppStateChange);

      return () => {
        subscription.remove();
      };
    }
  }, [appConfig, appConfigSuccess, isTranslationsLoaded]);

  if (!appConfig && appConfigError)
    return (
      <Background>
        <AppLoader
          loadingIndicator={false}
          text="Error loading application. Please close the application and try again, or contact support if the problem persists."
        />
      </Background>
    );

  if (!didUpdateCheck || !isTranslationsLoaded || !appConfigSuccess || !isAuthReady) {
    const appLoaderText = isTranslationsLoaded
      ? didUpdateCheck
        ? t('::Loading:Application')
        : loadingText
          ? t(loadingText)
          : undefined
      : undefined;

    return (
      <Background>
        <AppLoader
          loadingIndicator
          loadingProgress={isDownloadingUpdate ? updateDownloadProgress : undefined}
          text={appLoaderText}
        />
      </Background>
    );
  }

  return (
    <Background key={version}>
      <Stack
        screenOptions={{
          headerShown: false,
          contentStyle: { backgroundColor: 'transparent' }
        }}
      >
        <Stack.Protected guard={!isLoggedIn}>
          <Stack.Screen name="(auth)" />
        </Stack.Protected>
        <Stack.Protected guard={isLoggedIn && !isHost}>
          <Stack.Screen name="(tabs)" />
        </Stack.Protected>
        <Stack.Protected guard={isLoggedIn && isHost}>
          <Stack.Screen name="(admin)" />
        </Stack.Protected>
        <Stack.Screen name="modal" options={{ presentation: 'modal' }} />
        <Stack.Screen name="+not-found" />
      </Stack>
    </Background>
  );
}

export default Sentry.wrap(RootLayout);
